// Validation issue contract (CREATIVE-MODEL-V1.md §13). Shared by the editor,
// the API, the compiler and AI retries, so it is STABLE and machine-readable:
//
//   { code, layer, severity, path, entity?, message, params? }
//
// - `code` is the contract (UI copy and AI feedback key off it; never parse `message`).
// - `path` locates the value: "$.elements[3].frame.width", "$.layouts[0].overrides.logo".
// - `entity` names the document entity involved, when known ({ kind, id }).
// - `message` is a short deterministic technical sentence (English). It never echoes
//   user-provided free text; only ids, numbers and enum values appear in it.
// - `params` are bounded scalars (≤ 8 entries, strings ≤ 200 chars).
// - No stack traces, exception names or implementation details, ever.
//
// Layers mirror CREATIVE-MODEL §13.1. Severity "error" in the structure or
// reference layer blocks saving a DRAFT; any "error" blocks leaving DRAFT
// (submit/compile). "warning" never blocks.

export const ISSUE_LAYERS = [
  "structure",
  "reference",
  "animation",
  "interaction",
  "survey",
  "form",
  "game",
  "policy",
  "compile",
] as const;
export type IssueLayer = (typeof ISSUE_LAYERS)[number];

export const ISSUE_CODES = [
  // structure (L1)
  "NOT_PLAIN_JSON",
  "DOCUMENT_TOO_LARGE",
  "TOO_DEEP",
  "INVALID_TYPE",
  "REQUIRED",
  "UNKNOWN_FIELD",
  "EXECUTABLE_CONTENT_NOT_ALLOWED",
  "INVALID_VALUE",
  "INVALID_ENUM",
  "INVALID_FORMAT",
  "OUT_OF_RANGE",
  "TOO_SHORT",
  "TOO_LONG",
  "TOO_FEW",
  "TOO_MANY",
  "DUPLICATE_VALUE",
  "UNSUPPORTED_KIND",
  "UNSUPPORTED_SCHEMA_VERSION",
  "RESERVED_ID",
  "UNSAFE_URL",
  "UNSUPPORTED_MEDIA_TYPE",
  // reference (L2)
  "DUPLICATE_ID",
  "UNKNOWN_REFERENCE",
  "WRONG_REFERENCE_KIND",
  "MULTIPLE_PARENTS",
  "PARENT_CYCLE",
  "ORPHAN_ELEMENT",
  "DUPLICATE_FORMAT_KEY",
  "FORMAT_NOT_AVAILABLE",
  "FALLBACK_MISSING",
  "HTML5_EXECUTION_NOT_APPROVED",
  "FALLBACK_INVALID",
  "INVALID_COMPONENT",
  "INVALID_COMPONENT_VERSION",
  "COMPONENT_PROPS_INVALID",
  "SLOT_INVALID",
  "ASSET_KIND_MISMATCH",
  "OVERRIDE_NOT_APPLICABLE",
  "NOT_IN_LAYOUT",
  // animation (L3)
  "KEYFRAMES_INVALID",
  "TIMING_INVALID",
  "INFINITE_NOT_ALLOWED",
  "ANIMATION_OVERLAP",
  // interaction (L3)
  "TRIGGER_TARGET_INVALID",
  "ACTION_TARGET_INVALID",
  "ACTION_REQUIRES_GESTURE",
  "EXPRESSION_TYPE_MISMATCH",
  "EXPRESSION_TOO_COMPLEX",
  "RANDOM_NOT_ALLOWED_IN_CONDITION",
  "UNDECLARED_EVENT",
  "EVENT_PROPS_INVALID",
  "NESTING_TOO_DEEP",
  "INTERACTION_CYCLE",
  "POSSIBLE_INTERACTION_CYCLE",
  // advanced components (L3, 7.6E)
  "MEDIA_AUTOPLAY_DEGRADED",
  "MEDIA_NEVER_STARTS",
  "CAROUSEL_AUTOPLAY_TOO_FAST",
  "COMMAND_ARGS_INVALID",
  // survey / form (L3)
  "SURVEY_CYCLE",
  "SURVEY_PATH_TOO_LONG",
  "SURVEY_UNREACHABLE_QUESTION",
  "SURVEY_NO_EXIT",
  "SURVEY_PRESENTATION_INVALID",
  "SURVEY_STRUCTURE_GUIDANCE",
  "SURVEY_OPTIONS_INVALID",
  "PII_NOT_ALLOWED",
  "POSSIBLE_PII_REQUEST",
  "FORM_FIELD_NOT_PRESENTED",
  "FORM_NEVER_SUBMITTED",
  // game (L3)
  "TEMPLATE_INVALID",
  "BEHAVIOR_INVALID",
  "COLLIDER_MISSING",
  "VARIABLE_INITIAL_INVALID",
  // game module (L3, 7.6F)
  "GAME_ELEMENT_REUSED",
  "GAME_ELEMENT_OUTSIDE_SCENES",
  "GAME_TARGET_INVALID",
  "GAME_PAIRS_INVALID",
  "GAME_DROPS_INVALID",
  "GAME_WIN_INVALID",
  "GAME_RESULTS_INVALID",
  "GAME_SETUP_INVALID",
  "GAME_NEVER_ENDS",
  "GAME_NEVER_STARTS",
  "GAME_TARGET_UNLABELED",
  "GAME_SHUFFLE_SIZES_DIFFER",
  // runner (L3, 7.6F.1)
  "GAME_RUNNER_INVALID",
  "GAME_RUNNER_NO_TOUCH_CONTROLS",
  "GAME_RUNNER_LAYER_GAP",
  // policy (L4)
  "POLICY_LIMIT_EXCEEDED",
  // compile
  "UNSUPPORTED_RUNTIME_MAJOR",
  "DOCUMENT_INVALID",
] as const;
export type IssueCode = (typeof ISSUE_CODES)[number];

export type IssueSeverity = "error" | "warning";

export const ENTITY_KINDS = [
  "document",
  "layout",
  "scene",
  "element",
  "asset",
  "font",
  "link",
  "variable",
  "timer",
  "rule",
  "event",
  "survey",
  "question",
  "form",
  "field",
  "fallback",
  "game",
] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

export type IssueParams = Readonly<Record<string, string | number | boolean>>;

export interface ValidationIssue {
  readonly code: IssueCode;
  readonly layer: IssueLayer;
  readonly severity: IssueSeverity;
  readonly path: string;
  readonly entity?: { readonly kind: EntityKind; readonly id: string };
  readonly message: string;
  readonly params?: IssueParams;
}

export const MAX_ISSUES = 200;
const MAX_PARAMS = 8;
const MAX_PARAM_STRING = 200;

function boundParams(params: IssueParams | undefined): IssueParams | undefined {
  if (!params) return undefined;
  const entries = Object.entries(params).slice(0, MAX_PARAMS);
  if (entries.length === 0) return undefined;
  return Object.fromEntries(
    entries.map(([k, v]) => [
      k,
      typeof v === "string" && v.length > MAX_PARAM_STRING ? `${v.slice(0, MAX_PARAM_STRING)}…` : v,
    ])
  );
}

/** Collects issues for one layer. */
export class IssueSink {
  readonly #issues: ValidationIssue[];
  readonly #layer: IssueLayer;

  constructor(layer: IssueLayer, issues: ValidationIssue[] = []) {
    this.#layer = layer;
    this.#issues = issues;
  }

  /** A sink writing to the same list under another layer. */
  forLayer(layer: IssueLayer): IssueSink {
    return new IssueSink(layer, this.#issues);
  }

  add(code: IssueCode, path: string, message: string, params?: IssueParams, entity?: ValidationIssue["entity"]): void {
    this.#push("error", code, path, message, params, entity);
  }

  warn(code: IssueCode, path: string, message: string, params?: IssueParams, entity?: ValidationIssue["entity"]): void {
    this.#push("warning", code, path, message, params, entity);
  }

  get issues(): readonly ValidationIssue[] {
    return this.#issues;
  }

  get errorCount(): number {
    return this.#issues.filter((i) => i.severity === "error").length;
  }

  #push(
    severity: IssueSeverity,
    code: IssueCode,
    path: string,
    message: string,
    params: IssueParams | undefined,
    entity: ValidationIssue["entity"] | undefined
  ) {
    // Hard cap so hostile input cannot produce unbounded output; one extra slot marks truncation.
    if (this.#issues.length >= MAX_ISSUES) return;
    const bounded = boundParams(params);
    this.#issues.push({
      code,
      layer: this.#layer,
      severity,
      path,
      ...(entity ? { entity } : {}),
      message,
      ...(bounded ? { params: bounded } : {}),
    });
  }
}

const LAYER_ORDER = new Map(ISSUE_LAYERS.map((l, i) => [l, i]));

/** Deterministic order: errors first, then layer order, then path, then code. */
export function sortIssues(issues: readonly ValidationIssue[]): ValidationIssue[] {
  return [...issues].sort(
    (a, b) =>
      (a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1) ||
      (LAYER_ORDER.get(a.layer) ?? 0) - (LAYER_ORDER.get(b.layer) ?? 0) ||
      (a.path < b.path ? -1 : a.path > b.path ? 1 : 0) ||
      (a.code < b.code ? -1 : a.code > b.code ? 1 : 0)
  );
}
